<script setup>
// Fase 59 — a aba financeira do card: quanto este cliente já comprou, em
// quantos ciclos, e a linha do tempo de cada venda.
//
// O resumo NÃO é somado aqui: vem do backend, calculado sobre as colunas
// denormalizadas do card. Somar a página no cliente daria um total menor que o
// real assim que o histórico passasse de uma página.
//
// Carga, paginação e formatação vêm do composable compartilhado com o modal do
// board — as duas telas mostram o MESMO histórico e não podem divergir.
import { computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useOpportunityHistory } from '../../../composables/useOpportunityHistory';

const props = defineProps({
  item: { type: Object, required: true },
});

const { t } = useI18n();

const {
  opportunities,
  loading,
  loadingMore,
  loadFailed,
  appendFailed,
  recordedCount,
  hasMore,
  totalValue,
  averageTicket,
  formatMoney,
  formatDate,
  entryTitle,
  voidLine,
  load,
  loadMore,
  reset,
  voiding,
  voidFailed,
  canVoidEntry,
  voidingEntry,
  voidReason,
  startVoid,
  cancelVoid,
  confirmVoid,
} = useOpportunityHistory(toRef(props, 'item'));

const recordedList = computed(() =>
  opportunities.value.filter(entry => entry.status === 'recorded')
);

// Intervalo médio entre compras — a métrica de recompra. Precisa de pelo menos
// duas vendas para existir; com uma só não há intervalo a medir.
const averageRepurchaseDays = computed(() => {
  // Só calcula com o histórico COMPLETO na mão: medir o intervalo sobre uma
  // página daria um número errado (ou um falso "sem recompra") justamente nos
  // cards com mais vendas, que são os que interessam.
  if (hasMore.value) return null;

  const dates = recordedList.value
    .map(entry => new Date(entry.won_at).getTime())
    .filter(time => !Number.isNaN(time))
    .sort((a, b) => a - b);
  if (dates.length < 2) return null;

  const span = dates[dates.length - 1] - dates[0];
  return Math.round(span / (dates.length - 1) / 86400000);
});

// Rótulo pronto (em vez de ternário no template): evita "—" como texto cru,
// que o lint de i18n sinaliza — texto solto em template costuma ser string
// esquecida fora do dicionário.
const repurchaseLabel = computed(() => {
  // Com histórico paginado a métrica NÃO foi calculada — dizer "ainda sem
  // recompra" afirmaria o contrário do que o resumo do servidor mostra logo ao
  // lado (dezenas de vendas). Estado próprio, não o rótulo de ausência.
  if (hasMore.value) return t('PIPELINE.OPPORTUNITIES.REPURCHASE_PENDING');
  if (averageRepurchaseDays.value === null)
    return t('PIPELINE.OPPORTUNITIES.NO_REPURCHASE');
  return t('PIPELINE.OPPORTUNITIES.DAYS', averageRepurchaseDays.value, {
    named: { count: averageRepurchaseDays.value },
  });
});

// PRÓXIMO ciclo — só quando o histórico inteiro está carregado. A lista vem
// ordenada por `won_at`, não por ciclo, e o backfill pode ter dado um número
// alto a uma venda antiga: com página parcial, o maior ciclo pode estar fora da
// página e a tela anunciaria um número JÁ USADO como se fosse o próximo.
const currentCycle = computed(() => {
  if (hasMore.value) return null;

  const highest = opportunities.value.reduce(
    (max, entry) => Math.max(max, Number(entry.cycle_number) || 0),
    0
  );
  return highest + 1;
});

// Callback síncrono envolvendo `load()`: passar a função async direto faz o Vue
// tratá-la com `callWithAsyncErrorHandling`, que encaminha a rejeição ao error
// handler da aplicação ANTES do nosso estado de erro aparecer na tela.
watch(
  () => props.item?.id,
  () => {
    reset();
    load();
  },
  { immediate: true }
);

const entryMeta = entry => {
  const parts = [formatDate(entry.won_at)];
  if (entry.recorded_by_name) parts.push(entry.recorded_by_name);
  if (entry.cycle_number) {
    parts.push(
      t('PIPELINE.OPPORTUNITIES.CYCLE', { number: entry.cycle_number })
    );
  }
  return parts.join(' · ');
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <p v-if="loading" class="py-6 text-center text-body-main text-n-slate-11">
      {{ t('PIPELINE.OPPORTUNITIES.LOADING') }}
    </p>

    <p
      v-else-if="loadFailed"
      class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
      role="alert"
    >
      {{ t('PIPELINE.OPPORTUNITIES.LOAD_ERROR') }}
    </p>

    <template v-else>
      <!-- Resumo financeiro: realizado, ticket médio e recompra. -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <p class="text-label-small uppercase tracking-wide text-n-slate-10">
            {{ t('PIPELINE.OPPORTUNITIES.TOTAL_EARNED') }}
          </p>
          <p class="mt-1 text-heading-2 font-bold tabular-nums text-n-teal-11">
            {{ formatMoney(totalValue) }}
          </p>
          <p class="mt-0.5 text-label-small text-n-slate-11">
            {{ t('PIPELINE.OPPORTUNITIES.SUMMARY', recordedCount) }}
          </p>
        </div>

        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <p class="text-label-small uppercase tracking-wide text-n-slate-10">
            {{ t('PIPELINE.OPPORTUNITIES.AVERAGE_TICKET_LABEL') }}
          </p>
          <p class="mt-1 text-heading-2 font-bold tabular-nums text-n-slate-12">
            {{ formatMoney(averageTicket) }}
          </p>
        </div>

        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <p class="text-label-small uppercase tracking-wide text-n-slate-10">
            {{ t('PIPELINE.OPPORTUNITIES.REPURCHASE') }}
          </p>
          <p class="mt-1 text-heading-2 font-bold tabular-nums text-n-slate-12">
            {{ repurchaseLabel }}
          </p>
          <p
            v-if="currentCycle"
            class="mt-0.5 text-label-small text-n-slate-11"
          >
            {{ t('PIPELINE.OPPORTUNITIES.CYCLE', { number: currentCycle }) }}
          </p>
        </div>
      </div>

      <p
        v-if="opportunities.length === 0"
        class="py-6 text-center text-body-main text-n-slate-11"
      >
        {{ t('PIPELINE.OPPORTUNITIES.EMPTY') }}
      </p>

      <!-- Linha do tempo: cada venda, em ordem, com estorno marcado. -->
      <ol v-else class="flex flex-col gap-4 border-l-2 border-n-weak pl-5">
        <li
          v-for="entry in opportunities"
          :key="entry.id"
          class="relative"
          :class="{ 'opacity-60': entry.status === 'voided' }"
        >
          <span
            class="absolute -left-[27px] top-1.5 size-2.5 rounded-full ring-4 ring-n-solid-2"
            :class="entry.status === 'voided' ? 'bg-n-slate-8' : 'bg-n-teal-9'"
          />
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-label font-medium text-n-slate-12">
              {{ entryTitle(entry) }}
            </span>
            <span
              class="whitespace-nowrap text-label font-bold tabular-nums"
              :class="
                entry.status === 'voided'
                  ? 'text-n-slate-10 line-through'
                  : 'text-n-teal-11'
              "
            >
              {{ formatMoney(entry.total_value) }}
            </span>
          </div>
          <p class="mt-0.5 text-label-small text-n-slate-10">
            {{ entryMeta(entry) }}
          </p>
          <p
            v-if="entry.status === 'voided'"
            class="mt-0.5 text-label-small text-n-ruby-11"
          >
            {{ voidLine(entry) }}
          </p>

          <!-- Estornar também daqui: esta aba mostra o MESMO histórico do modal
               do board, e sem a ação quem abre o card não teria como corrigir
               uma venda. A regra vive no composable; só o layout difere. -->
          <Button
            v-if="canVoidEntry(entry)"
            class="mt-1"
            variant="ghost"
            color="ruby"
            size="sm"
            :label="t('PIPELINE.OPPORTUNITIES.VOID')"
            :disabled="voiding !== null"
            @click="startVoid(entry)"
          />
          <div
            v-if="voidingEntry === entry.id"
            class="mt-2 rounded-lg bg-n-alpha-1 p-3"
          >
            <p class="mb-2 text-label-small text-n-slate-11">
              {{ t('PIPELINE.OPPORTUNITIES.VOID_HINT') }}
            </p>
            <Input
              v-model="voidReason"
              type="text"
              :placeholder="t('PIPELINE.OPPORTUNITIES.VOID_REASON_PLACEHOLDER')"
              :disabled="voiding !== null"
            />
            <div class="mt-2 flex justify-end gap-2">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                :label="t('PIPELINE.OPPORTUNITIES.VOID_CANCEL')"
                :disabled="voiding !== null"
                @click="cancelVoid"
              />
              <Button
                color="ruby"
                size="sm"
                :label="t('PIPELINE.OPPORTUNITIES.VOID_CONFIRM')"
                :is-loading="voiding === entry.id"
                :disabled="voiding !== null"
                @click="confirmVoid(entry)"
              />
            </div>
            <p
              v-if="voidFailed === entry.id"
              class="mt-2 text-sm text-n-ruby-11"
              role="alert"
            >
              {{ t('PIPELINE.OPPORTUNITIES.VOID_ERROR') }}
            </p>
          </div>
        </li>
      </ol>

      <p
        v-if="appendFailed"
        class="text-center text-sm text-n-ruby-11"
        role="alert"
      >
        {{ t('PIPELINE.OPPORTUNITIES.LOAD_MORE_ERROR') }}
      </p>
      <div v-if="hasMore && !loading" class="flex justify-center">
        <Button
          variant="outline"
          color="slate"
          size="sm"
          :label="
            appendFailed
              ? t('PIPELINE.OPPORTUNITIES.RETRY')
              : t('PIPELINE.OPPORTUNITIES.LOAD_MORE')
          "
          :is-loading="loadingMore"
          :disabled="loadingMore"
          @click="loadMore"
        />
      </div>
    </template>
  </div>
</template>
